import React, { FC, KeyboardEventHandler, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { getUser, setActiveScholar } from 'actions/users';
import { getUnits } from 'actions/scholars';
import { BrowserRouter as Router } from 'react-router-dom';
import { IRootState } from 'store';
import MainRouter from 'routes';
import _ from 'lodash';
import LiteracyModal from 'components/common/literacy-modal';
import { initAmplitude } from 'services/amplitude';
import MobileService from 'services/mobile';
import { clearGlobalError, setGlobalError } from 'actions/app-state';
import close from 'style/imgs/icons/close-icon.svg';
import 'font-proxima-nova/style.css';

initAmplitude();
// Root component
const mapStateToProps = (state: IRootState) => ({
  userData: state.users.userData,
  auth: state.auth,
  spinner: state.spinner,
  scholars: state.scholars,
  literacyModalActive: state.appState.literacyModalActive,
  globalError: state.appState.globalError,
});

type Props = ReturnType<typeof mapStateToProps>;

const App: FC<Props> = ({
  userData,
  auth,
  spinner,
  scholars,
  literacyModalActive,
  globalError,
}) => {
  const dispatch = useDispatch();

  const { isSignedIn } = auth;
  const unitsReady = scholars.units !== null;
  const hasChildren = unitsReady && scholars.units?.length !== 0;

  const [isActiveSubscription] = useState<boolean>(true);

  useEffect(() => {
    if (!userData?.email && isSignedIn) {
      dispatch(getUser());
      dispatch(getUnits());
    }
  }, [dispatch, userData, auth, isSignedIn]);

  useEffect(() => {
    if (scholars.units === null || scholars.units?.length === 0) {
      return;
    }

    const savedScholarId = localStorage.getItem('activeScholarId') || '';
    const kid =
      _.find(scholars.units, { id: +savedScholarId }) || scholars.units[0];
    if (isSignedIn) {
      if (kid) {
        dispatch(setActiveScholar(kid.id));
      } else {
        dispatch(setActiveScholar(scholars.units[0].id));
      }
    }
  }, [dispatch, scholars.units, isSignedIn]);

  useEffect(() => {
    document.title = 'Aneta';
  }, []);

  useEffect(() => {
    if (isSignedIn) {
      MobileService.requestPermissions().then((p) => {
        if (p === 'denied' || !p) {
          const msg =
            p === 'denied'
              ? 'You have notifications disabled in your browser. Please, go to browser settings and enable it.'
              : "We are sorry, your browser doesn't support push notifications.";

          dispatch(setGlobalError(msg));
        }
      });
    }
  }, [isSignedIn, dispatch]);

  const closeError = () => {
    dispatch(clearGlobalError());
  };

  const onKeyDown: KeyboardEventHandler<HTMLSpanElement> = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      closeError();
    }
  };

  // TODO: check Subscription

  // useEffect(() => {
  //   if (userData?.subscriptionStatus) {
  //     const { subscriptionStatus, type } = userData;
  //     setSubscriptionStatus(
  //       subscriptionStatus === UserSubscriptionStatus.TRIALING ||
  //         subscriptionStatus === UserSubscriptionStatus.ACTIVE ||
  //         subscriptionStatus === UserSubscriptionStatus.CANCELING ||
  //         type === 'teacher'
  //     );
  //   }
  // }, [userData]);

  return (
    <Router>
      {globalError && (
        <span className="message-text" id="global-error">
          <div
            onClick={closeError}
            onKeyDown={onKeyDown}
            tabIndex={0}
            role="button"
          >
            <img src={close} alt="close icon" />
          </div>
          {globalError}
        </span>
      )}
      {(unitsReady || !isSignedIn) && (
        <MainRouter
          hasChildren={hasChildren}
          userType={userData?.type || ''}
          isSpinnerActive={spinner}
          isActiveSubscription={isActiveSubscription}
        />
      )}
      {literacyModalActive && <LiteracyModal />}
    </Router>
  );
};
export default connect(mapStateToProps)(App);
